/**
 * TtsSegmenter
 *
 * Segments a streamed assistant reply into per-request chunks for the
 * server TTS auto-play pipeline (POST /api/speech/tts — one request per
 * segment). Wraps SentenceChunker (same sentence-boundary rules) and adds
 * two TTS-specific behaviors:
 *
 * 1. First-clause early cut — until the first segment has been emitted,
 *    a clause boundary (`,` or `;` followed by whitespace) at index
 *    >= FIRST_CLAUSE_MIN_CHARS cuts the segment early. Long opening
 *    sentences otherwise dominate time-to-first-audio; the cost is a
 *    small prosody seam at the comma. Never applies after the first
 *    segment.
 *
 * 2. Hard length cap — no segment may reach MAX_TTS_SEGMENT_CHARS
 *    (safely below the endpoint's 800-char contract limit). Over-long
 *    sentences and punctuation-free run-ons are split at the last word
 *    boundary before the cap (mid-word only if a single "word" exceeds
 *    the cap).
 *
 * Spec: docs/specs/mobile/server-autoplay-tts.mobile.md (ATTS-001)
 */

import { SentenceChunker } from './sentenceChunker';

/**
 * Upper bound on a single segment. The POST /api/speech/tts contract caps
 * `text` at 800 chars; 600 leaves headroom and keeps worst-case per-segment
 * synthesis latency bounded.
 */
export const MAX_TTS_SEGMENT_CHARS = 600;

/** Minimum chars before the first-clause early cut may trigger. */
export const FIRST_CLAUSE_MIN_CHARS = 30;

/**
 * Find a clause boundary (`,` or `;` followed by whitespace) at or after
 * `minIndex`. Returns the boundary char's index, or -1. The whitespace
 * requirement avoids cutting inside numbers like "1,000".
 */
function findClauseBoundary(text: string, minIndex: number): number {
  for (let i = minIndex; i < text.length - 1; i++) {
    const char = text[i];
    if ((char === ',' || char === ';') && /\s/.test(text[i + 1])) {
      return i;
    }
  }
  return -1;
}

/**
 * Split `text` into pieces shorter than `max`, cutting at the last word
 * boundary before the limit (mid-word only when a single word exceeds it).
 */
function splitAtWordBoundaries(text: string, max: number): string[] {
  const pieces: string[] = [];
  let rest = text.trim();
  while (rest.length >= max) {
    let cut = rest.lastIndexOf(' ', max - 1);
    if (cut <= 0) cut = max - 1; // single over-long word — hard cut
    const piece = rest.slice(0, cut + 1).trim();
    if (piece) pieces.push(piece);
    rest = rest.slice(cut + 1).trim();
  }
  if (rest) pieces.push(rest);
  return pieces;
}

/**
 * Segment a COMPLETE text into sentence chunks for tap-to-replay.
 *
 * Sentence boundaries ONLY — deliberately NO first-clause early cut (that's a
 * `TtsSegmenter` behavior for shaving auto-play's first-audio latency). A
 * neural voice plans its intonation over the whole unit it's given, so cutting
 * mid-sentence flattens prosody and leaves a seam at the rejoin; sentences are
 * the natural prosodic unit, so per-sentence chunks keep their intonation while
 * still letting replay start sentence 1 without waiting for a long monolith.
 * Over-long sentences are split at word boundaries below the contract cap.
 *
 * A single-sentence (short) reply returns one chunk = the pre-chunking
 * behavior, with full prosody preserved.
 */
export function segmentIntoSentences(text: string): string[] {
  const chunker = new SentenceChunker();
  const sentences = chunker.feed(text);
  const tail = chunker.flush();
  if (tail) sentences.push(tail);
  return sentences.flatMap((s) => splitAtWordBoundaries(s, MAX_TTS_SEGMENT_CHARS));
}

export class TtsSegmenter {
  private chunker = new SentenceChunker();
  private emittedFirst = false;

  /**
   * Feed a streamed token. Returns zero or more complete segments, in
   * speech order, each below MAX_TTS_SEGMENT_CHARS.
   */
  feed(token: string): string[] {
    const out: string[] = [];

    for (const sentence of this.chunker.feed(token)) {
      out.push(...splitAtWordBoundaries(sentence, MAX_TTS_SEGMENT_CHARS));
    }

    // First-clause early cut: nothing emitted yet and no sentence boundary
    // in the buffer — cut at the first clause boundary past the minimum.
    if (!this.emittedFirst && out.length === 0) {
      const boundary = findClauseBoundary(this.chunker.pending, FIRST_CLAUSE_MIN_CHARS - 1);
      if (boundary !== -1) {
        const cut = this.chunker.cutAt(boundary);
        if (cut) out.push(...splitAtWordBoundaries(cut, MAX_TTS_SEGMENT_CHARS));
      }
    }

    // Run-on cap: a buffer growing past the cap with no boundary in sight
    // is force-split so no future segment can exceed the contract limit.
    while (this.chunker.pending.length >= MAX_TTS_SEGMENT_CHARS) {
      let cut = this.chunker.pending.lastIndexOf(' ', MAX_TTS_SEGMENT_CHARS - 1);
      if (cut <= 0) cut = MAX_TTS_SEGMENT_CHARS - 1;
      const piece = this.chunker.cutAt(cut);
      if (piece) out.push(piece);
    }

    if (out.length > 0) this.emittedFirst = true;
    return out;
  }

  /**
   * Flush the remaining buffered text (stream end). Returns zero or more
   * final segments.
   */
  flush(): string[] {
    const remaining = this.chunker.flush();
    if (!remaining) return [];
    this.emittedFirst = true;
    return splitAtWordBoundaries(remaining, MAX_TTS_SEGMENT_CHARS);
  }

  /** Reset all buffered state (cancel). */
  reset(): void {
    this.chunker.reset();
    this.emittedFirst = false;
  }
}
